import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Download,
  Search,
  RefreshCw,
  X,
  Save,
  ChevronDown,
  AlertCircle,
  Check
} from 'lucide-react';
import type { User } from '../../types';
import { todayLocal } from '../../utils/localDate';

interface ClientModuleDeployment {
  id: string;
  clientId: string;
  clientName: string;
  moduleId: string;
  moduleName: string;
  nodeVersion: string;
  branch: string;
  // URL path segment (relative to the college's domain) where this module is
  // actually deployed/accessible for this client, e.g. "/hostel".
  subpath: string;
  environment: 'development' | 'staging' | 'production';
  mongodbVersion: string;
  keycloakVersion: string;
  status: 'active' | 'inactive' | 'testing' | 'deprecated';
  deploymentDate: string;
  deployedBy?: { name?: string; email?: string };
  notes?: string;
  lastUpdatedBy?: { name?: string; email?: string };
}

interface Institute {
  _id: string;
  name: string;
  description?: string;
  address?: string;
}

interface ModuleItem {
  _id: string;
  name: string;
  description?: string;
  owner?: string;
}

interface ModuleDeploymentInventoryProps {
  currentUser: User;
}

const ModuleDeploymentInventory: React.FC<ModuleDeploymentInventoryProps> = ({ currentUser }) => {
  const [deployments, setDeployments] = useState<ClientModuleDeployment[]>([]);
  const [filteredDeployments, setFilteredDeployments] = useState<ClientModuleDeployment[]>([]);
  const [institutes, setInstitutes] = useState<Institute[]>([]);
  const [modules, setModules] = useState<ModuleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [selectedClient, setSelectedClient] = useState('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [envFilter, setEnvFilter] = useState('all');
  const [expandedInstitutes, setExpandedInstitutes] = useState<Set<string>>(new Set());

  const [activeTab, setActiveTab] = useState<'all' | 'college'>('all');
  const [selectedCollege, setSelectedCollege] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Partial<ClientModuleDeployment> | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);

  // Track module-specific versions/branches/subpaths
  const [moduleVersionMap, setModuleVersionMap] = useState<Record<string, { nodeVersion: string; branch: string; subpath: string }>>({});
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  const [newDeployment, setNewDeployment] = useState<Partial<ClientModuleDeployment>>({
    clientName: '',
    moduleName: '',
    nodeVersion: '',
    branch: '',
    subpath: '',
    environment: 'production',
    mongodbVersion: '',
    keycloakVersion: '',
    status: 'active',
    deploymentDate: todayLocal(),
    notes: ''
  });

  const canEdit = ['pm', 'tl', 'devops', 'admin'].includes(currentUser.role);

  useEffect(() => {
    fetchInventory();
    fetchClientsAndModules();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [deployments, selectedClient, search, statusFilter, envFilter]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch('/api/client-module-deployments');
      if (!response.ok) throw new Error('Failed to fetch inventory');

      const data = await response.json();
      setDeployments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch inventory');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsAndModules = async () => {
    try {
      const [institutesRes, modulesRes] = await Promise.all([
        fetch('/api/institutes'),
        fetch('/api/modules')
      ]);

      if (institutesRes.ok) {
        const institutesData = await institutesRes.json();
        setInstitutes(Array.isArray(institutesData) ? institutesData : []);
      }

      if (modulesRes.ok) {
        const modulesData = await modulesRes.json();
        setModules(Array.isArray(modulesData) ? modulesData : []);
      }
    } catch (err) {
      console.error('Error fetching institutes/modules:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...deployments];

    if (selectedClient !== 'all') {
      filtered = filtered.filter(d => d.clientName === selectedClient);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }

    if (envFilter !== 'all') {
      filtered = filtered.filter(d => d.environment === envFilter);
    }

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(d =>
        d.moduleName.toLowerCase().includes(searchLower) ||
        d.clientName.toLowerCase().includes(searchLower) ||
        d.branch.toLowerCase().includes(searchLower) ||
        d.nodeVersion.toLowerCase().includes(searchLower)
      );
    }

    setFilteredDeployments(filtered);
  };

  const handleSaveNew = async () => {
    if (!newDeployment.clientName || selectedModules.length === 0) {
      setError('Please select institute and at least one module');
      return;
    }

    // Check that all selected modules have versions and branches
    for (const moduleId of selectedModules) {
      const versionData = moduleVersionMap[moduleId];
      if (!versionData?.nodeVersion || !versionData?.branch) {
        setError(`Please enter Node Version and Branch for all selected modules`);
        return;
      }
    }

    try {
      // Create a record for each selected module
      const recordsToCreate = selectedModules.map(moduleId => {
        const module = modules.find(m => m._id === moduleId);
        const versionData = moduleVersionMap[moduleId];
        
        return {
          clientName: newDeployment.clientName,
          clientId: newDeployment.clientName?.toLowerCase().replace(/\s+/g, '_'),
          moduleId: module?._id,
          moduleName: module?.name,
          nodeVersion: versionData.nodeVersion,
          branch: versionData.branch,
          subpath: versionData.subpath || '',
          environment: newDeployment.environment,
          mongodbVersion: newDeployment.mongodbVersion || '',
          keycloakVersion: newDeployment.keycloakVersion || '',
          status: newDeployment.status,
          deploymentDate: newDeployment.deploymentDate,
          deployedBy: currentUser,
          notes: newDeployment.notes || ''
        };
      });

      // Save each record. A network failure on one must not abort the others'
      // bookkeeping — previously Promise.all rejected on the first failure while
      // the other requests had already been saved server-side, so they never
      // reached the table and a retry then created duplicates of them.
      const settled = await Promise.all(
        recordsToCreate.map(async (record, i) => {
          try {
            const res = await fetch('/api/client-module-deployments', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(record)
            });
            if (!res.ok) return { ok: false as const, moduleId: selectedModules[i] };
            return { ok: true as const, saved: await res.json(), moduleId: selectedModules[i] };
          } catch {
            return { ok: false as const, moduleId: selectedModules[i] };
          }
        })
      );

      const savedRecords = settled.filter(r => r.ok).map(r => (r as any).saved);
      const failed = settled.filter(r => !r.ok);
      if (savedRecords.length > 0) {
        setDeployments([...deployments, ...savedRecords]);
      }

      if (failed.length > 0) {
        // Keep the form open with ONLY the modules that failed selected, so
        // retrying re-submits just those instead of duplicating the saved ones.
        const failedIds = failed.map(f => f.moduleId);
        setSelectedModules(failedIds);
        const failedNames = failedIds.map(id => modules.find(m => m._id === id)?.name || id).join(', ');
        setError(
          savedRecords.length > 0
            ? `${savedRecords.length} saved, but ${failed.length} failed (${failedNames}). Those are still selected — press Save again to retry just them.`
            : `Failed to save: ${failedNames}`
        );
        return;
      }

      setError('');
      setSuccess(`${selectedModules.length} deployment(s) added successfully`);
      setShowNewForm(false);
      setSelectedModules([]);
      setModuleVersionMap({});
      setNewDeployment({
        clientName: '',
        moduleName: '',
        nodeVersion: '',
        branch: '',
        subpath: '',
        environment: 'production',
        mongodbVersion: '',
        keycloakVersion: '',
        status: 'active',
        deploymentDate: todayLocal(),
        notes: ''
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save deployments');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingData) return;

    try {
      const response = await fetch(`/api/client-module-deployments/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editingData,
          lastUpdatedBy: currentUser
        })
      });

      if (!response.ok) throw new Error('Failed to update deployment');

      const updated = await response.json();
      setDeployments(deployments.map(d => d.id === editingId ? updated : d));
      setSuccess('Deployment updated successfully');
      setEditingId(null);
      setEditingData(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update deployment');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this deployment record?')) return;

    try {
      const response = await fetch(`/api/client-module-deployments/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete deployment');

      setDeployments(deployments.filter(d => d.id !== id));
      setSuccess('Deployment record deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete deployment');
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch('/api/client-module-deployments/export/csv');
      if (!response.ok) throw new Error('Failed to export');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `module-deployments-${todayLocal()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess('Export completed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to export data');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300',
      inactive: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 border-gray-300',
      testing: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-300',
      deprecated: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-300'
    };
    return colors[status] || colors.active;
  };

  const groupDeploymentsByInstitute = () => {
    const grouped: Record<string, ClientModuleDeployment[]> = {};
    filteredDeployments.forEach(deployment => {
      if (!grouped[deployment.clientName]) {
        grouped[deployment.clientName] = [];
      }
      grouped[deployment.clientName].push(deployment);
    });
    return grouped;
  };

  const toggleExpandInstitute = (instituteName: string) => {
    const newExpanded = new Set(expandedInstitutes);
    if (newExpanded.has(instituteName)) {
      newExpanded.delete(instituteName);
    } else {
      newExpanded.add(instituteName);
    }
    setExpandedInstitutes(newExpanded);
  };

  // Shared row renderer — used by both the "All Deployments" grouped table
  // and the "By College" detail view, so inline edit/delete behaves
  // identically in both places without duplicating this JSX.
  const renderDeploymentRow = (deployment: ClientModuleDeployment, options?: { showClientCell?: boolean }) => (
    <tr key={deployment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
      {options?.showClientCell !== false && (
        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white pl-12"></td>
      )}
      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{deployment.moduleName}</td>
      <td className="px-6 py-4 text-sm">
        {editingId === deployment.id ? (
          <input
            type="text"
            value={editingData?.nodeVersion || ''}
            onChange={(e) => setEditingData({ ...editingData, nodeVersion: e.target.value })}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-20"
          />
        ) : (
          <span className="font-mono text-blue-600 dark:text-blue-400">v{deployment.nodeVersion}</span>
        )}
      </td>
      <td className="px-6 py-4 text-sm">
        {editingId === deployment.id ? (
          <input
            type="text"
            value={editingData?.branch || ''}
            onChange={(e) => setEditingData({ ...editingData, branch: e.target.value })}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-24"
          />
        ) : (
          <span className="inline-block px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
            {deployment.branch}
          </span>
        )}
      </td>
      <td className="px-6 py-4 text-sm">
        {editingId === deployment.id ? (
          <input
            type="text"
            value={editingData?.subpath || ''}
            onChange={(e) => setEditingData({ ...editingData, subpath: e.target.value })}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-24"
            placeholder="/hostel"
          />
        ) : (
          <span className="font-mono text-xs text-gray-600 dark:text-gray-400">{deployment.subpath || '—'}</span>
        )}
      </td>
      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
        {editingId === deployment.id ? (
          <select
            value={editingData?.environment || 'production'}
            onChange={(e) => setEditingData({ ...editingData, environment: e.target.value as any })}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="development">Dev</option>
            <option value="staging">Staging</option>
            <option value="production">Prod</option>
          </select>
        ) : (
          deployment.environment.substring(0, 4)
        )}
      </td>
      <td className="px-6 py-4 text-xs bg-blue-50 dark:bg-blue-900/10 rounded">
        {editingId === deployment.id ? (
          <div className="flex flex-col gap-1">
            <input
              type="text"
              placeholder="MongoDB"
              value={editingData?.mongodbVersion || ''}
              onChange={(e) => setEditingData({ ...editingData, mongodbVersion: e.target.value })}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs w-32"
            />
            <input
              type="text"
              placeholder="Keycloak"
              value={editingData?.keycloakVersion || ''}
              onChange={(e) => setEditingData({ ...editingData, keycloakVersion: e.target.value })}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs w-32"
            />
          </div>
        ) : (
          <div className="space-y-1">
            {deployment.mongodbVersion ? (
              <span className="block text-gray-900 dark:text-white font-semibold">MongoDB: {deployment.mongodbVersion}</span>
            ) : (
              <span className="block text-gray-500 dark:text-gray-400">MongoDB: —</span>
            )}
            {deployment.keycloakVersion ? (
              <span className="block text-gray-900 dark:text-white font-semibold">Keycloak: {deployment.keycloakVersion}</span>
            ) : (
              <span className="block text-gray-500 dark:text-gray-400">Keycloak: —</span>
            )}
          </div>
        )}
      </td>
      <td className="px-6 py-4 text-sm">
        {editingId === deployment.id ? (
          <select
            value={editingData?.status || 'active'}
            onChange={(e) => setEditingData({ ...editingData, status: e.target.value as any })}
            className={`px-2 py-1 border rounded text-xs font-medium ${getStatusColor(editingData?.status || 'active')}`}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="testing">Testing</option>
            <option value="deprecated">Deprecated</option>
          </select>
        ) : (
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(deployment.status)}`}>
            {deployment.status}
          </span>
        )}
      </td>
      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
        {new Date(deployment.deploymentDate).toLocaleDateString()}
      </td>
      {canEdit && (
        <td className="px-6 py-4 text-center">
          <div className="flex items-center justify-center gap-2">
            {editingId === deployment.id ? (
              <>
                <button
                  onClick={handleSaveEdit}
                  className="p-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded hover:bg-green-200"
                >
                  <Save className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditingId(null)}
                  className="p-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setEditingId(deployment.id);
                    setEditingData(deployment);
                  }}
                  className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(deployment.id)}
                  className="p-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded hover:bg-red-200"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Module Deployment Inventory</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Backup & tracking of deployed modules across clients</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchInventory}
            disabled={loading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title="Export to CSV"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          {canEdit && (
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Deployment
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600" />
          <p className="text-green-700 dark:text-green-400">{success}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 flex gap-6">
        <button
          onClick={() => setActiveTab('all')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'all'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          All Deployments
        </button>
        <button
          onClick={() => { setActiveTab('college'); setSelectedCollege(null); }}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'college'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          By College
        </button>
      </div>

      {/* New Deployment Form */}
      {showNewForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold">Add New Deployment</h2>
            <button onClick={() => setShowNewForm(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Institute *</label>
              <select
                value={newDeployment.clientName || ''}
                onChange={(e) => setNewDeployment({ ...newDeployment, clientName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">-- Select an Institute --</option>
                {institutes.map(inst => (
                  <option key={inst._id} value={inst.name}>{inst.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Module Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Select Modules *</label>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              {modules.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">No modules available</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {modules.map(module => (
                    <label key={module._id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={selectedModules.includes(module._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedModules([...selectedModules, module._id]);
                            setModuleVersionMap({
                              ...moduleVersionMap,
                              [module._id]: { nodeVersion: '', branch: '', subpath: '' }
                            });
                          } else {
                            setSelectedModules(selectedModules.filter(m => m !== module._id));
                            const newMap = { ...moduleVersionMap };
                            delete newMap[module._id];
                            setModuleVersionMap(newMap);
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-gray-700 dark:text-gray-300">{module.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Module-specific version and branch fields */}
          {selectedModules.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Module Details</h3>
              <div className="space-y-4">
                {selectedModules.map(moduleId => {
                  const module = modules.find(m => m._id === moduleId);
                  const versionData = moduleVersionMap[moduleId] || { nodeVersion: '', branch: '', subpath: '' };

                  return (
                    <div key={moduleId} className="pb-4 border-b border-blue-200 dark:border-blue-800 last:border-b-0">
                      <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">{module?.name}</label>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Node Version *</label>
                          <input
                            type="text"
                            value={versionData.nodeVersion}
                            onChange={(e) => setModuleVersionMap({
                              ...moduleVersionMap,
                              [moduleId]: { ...versionData, nodeVersion: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="e.g., 22.21.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Branch *</label>
                          <input
                            type="text"
                            value={versionData.branch}
                            onChange={(e) => setModuleVersionMap({
                              ...moduleVersionMap,
                              [moduleId]: { ...versionData, branch: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="e.g., main, develop"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Subpath</label>
                          <input
                            type="text"
                            value={versionData.subpath}
                            onChange={(e) => setModuleVersionMap({
                              ...moduleVersionMap,
                              [moduleId]: { ...versionData, subpath: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="e.g., /hostel"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Common fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Environment</label>
              <select
                value={newDeployment.environment || 'production'}
                onChange={(e) => setNewDeployment({ ...newDeployment, environment: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="development">Development</option>
                <option value="staging">Staging</option>
                <option value="production">Production</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={newDeployment.status || 'active'}
                onChange={(e) => setNewDeployment({ ...newDeployment, status: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="testing">Testing</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">MongoDB Version</label>
              <input
                type="text"
                value={newDeployment.mongodbVersion || ''}
                onChange={(e) => setNewDeployment({ ...newDeployment, mongodbVersion: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., 5.0.0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Keycloak Version</label>
              <input
                type="text"
                value={newDeployment.keycloakVersion || ''}
                onChange={(e) => setNewDeployment({ ...newDeployment, keycloakVersion: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., 20.0.0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Deployment Date</label>
              <input
                type="date"
                value={newDeployment.deploymentDate?.split('T')[0] || ''}
                onChange={(e) => setNewDeployment({ ...newDeployment, deploymentDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
            <textarea
              value={newDeployment.notes || ''}
              onChange={(e) => setNewDeployment({ ...newDeployment, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Any additional notes..."
            />
          </div>

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowNewForm(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveNew}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Deployment
            </button>
          </div>
        </div>
      )}

      {activeTab === 'all' && (
      <>
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
        <div className="flex gap-4 flex-wrap items-center">
          <div className="flex-1 min-w-xs relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search modules, clients, branches..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Institutes</option>
            {institutes.map(inst => (
              <option key={inst._id} value={inst.name}>{inst.name}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="testing">Testing</option>
            <option value="deprecated">Deprecated</option>
          </select>

          <select
            value={envFilter}
            onChange={(e) => setEnvFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Environments</option>
            <option value="development">Development</option>
            <option value="staging">Staging</option>
            <option value="production">Production</option>
          </select>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing {filteredDeployments.length} of {deployments.length} records
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      )}

      {/* Table */}
      {!loading && filteredDeployments.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Module</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Node Version</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Branch</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Subpath</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Env</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Versions</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Deployed</th>
                  {canEdit && <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {Object.entries(groupDeploymentsByInstitute()).map(([instituteName, modules]) => (
                  <React.Fragment key={instituteName}>
                    {/* Institute Header Row */}
                    <tr className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-150 dark:hover:bg-gray-650 cursor-pointer" onClick={() => toggleExpandInstitute(instituteName)}>
                      <td colSpan={canEdit ? 10 : 9} className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <ChevronDown
                            className={`w-5 h-5 text-gray-700 dark:text-gray-300 transition-transform ${expandedInstitutes.has(instituteName) ? 'rotate-180' : ''}`}
                          />
                          <span className="font-semibold text-gray-900 dark:text-white">{instituteName}</span>
                          <span className="ml-4 inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                            {modules.length} module{modules.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </td>
                    </tr>

                    {/* Module Rows (shown when expanded) */}
                    {expandedInstitutes.has(instituteName) && modules.map((deployment) => renderDeploymentRow(deployment))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredDeployments.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">No deployments found</p>
          {canEdit && (
            <button
              onClick={() => setShowNewForm(true)}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Add first deployment
            </button>
          )}
        </div>
      )}
      </>
      )}

      {activeTab === 'college' && selectedCollege === null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {institutes.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 col-span-full text-center py-8">No colleges found</p>
          )}
          {institutes.map(inst => {
            const moduleCount = deployments.filter(d => d.clientName === inst.name).length;
            return (
              <button
                key={inst._id}
                onClick={() => setSelectedCollege(inst.name)}
                className="text-left bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 hover:shadow-lg hover:border-blue-300 border border-transparent transition-all"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white">{inst.name}</h3>
                {inst.address && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{inst.address}</p>}
                <span className="inline-block mt-3 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  {moduleCount} module{moduleCount !== 1 ? 's' : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {activeTab === 'college' && selectedCollege !== null && (() => {
        const collegeDeployments = deployments.filter(d => d.clientName === selectedCollege);
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setSelectedCollege(null)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                ← Back to colleges
              </button>
              {canEdit && (
                <button
                  onClick={() => {
                    setNewDeployment({ ...newDeployment, clientName: selectedCollege });
                    setShowNewForm(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Add Module
                </button>
              )}
            </div>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedCollege}</h2>

            {collegeDeployments.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
                <p className="text-gray-600 dark:text-gray-400">No modules deployed to this college yet</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Module</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Node Version</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Branch</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Subpath</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Env</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Versions</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Deployed</th>
                        {canEdit && <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Actions</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {collegeDeployments.map((deployment) => renderDeploymentRow(deployment, { showClientCell: false }))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Permissions Info */}
      {!canEdit && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            View-only access. Contact PM, TL, or DevOps team to manage inventory.
          </p>
        </div>
      )}
    </div>
  );
};

export default ModuleDeploymentInventory;
